import { useEffect, useRef, useState } from "react";
import { Plus, Search, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export default function Typeahead({ endpoint, value, onSelect, placeholder, meta, onAddNew, testid, disabled, labelKey = "name", nav, autoFocus }) {
  const [text, setText] = useState(value?.[labelKey] || "");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [hi, setHi] = useState(0);
  const box = useRef(null);

  useEffect(() => { setText(value?.[labelKey] || ""); }, [value, labelKey]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      const { data } = await api.get(endpoint, { params: { q: text, status: "active", limit: 12 } });
      setRows(data);
      setHi(value ? -1 : 0);
    }, 150);
    return () => clearTimeout(t);
  }, [text, open, endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (r) => { onSelect(r); setText(r[labelKey]); setOpen(false); };
  const total = rows.length + (onAddNew ? 1 : 0);
  const onKey = (e) => {
    if (e.key === "Tab" || e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, total - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter" && open) {
      if (rows[hi]) pick(rows[hi]);
      else if (onAddNew && hi === rows.length) { e.preventDefault(); e.stopPropagation(); setOpen(false); onAddNew(text); }
      else setOpen(false);
    }
  };

  useEffect(() => {
    box.current?.querySelector(`[data-hi="1"]`)?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  return (
    <div className="relative" ref={box}>
      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <Input value={text} disabled={disabled} placeholder={placeholder} className="pl-9 h-10" data-testid={testid} data-nav={nav ? "" : undefined} autoFocus={autoFocus} autoComplete="off"
        onFocus={() => setOpen(true)} onKeyDown={onKey}
        onChange={(e) => { setText(e.target.value); setOpen(true); if (value) onSelect(null); }} />
      {value && <Check className="w-4 h-4 text-emerald-600 absolute right-3 top-1/2 -translate-y-1/2" />}
      {open && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto" data-testid={`${testid}-options`}>
          {rows.map((r, i) => (
            <button type="button" key={r.id} onMouseDown={(e) => { e.preventDefault(); pick(r); }} data-testid={`${testid}-option-${i}`} data-hi={i === hi ? "1" : undefined} tabIndex={-1}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${i === hi ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
              <span className="text-sm text-slate-800 truncate">{r[labelKey]}</span>
              <span className="num text-[11px] text-slate-400 shrink-0">{meta(r)}</span>
            </button>
          ))}
          {!rows.length && <div className="px-3 py-3 text-xs text-slate-400">No matches</div>}
          {onAddNew && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); setOpen(false); onAddNew(text); }} data-testid={`${testid}-add-new`} data-hi={hi === rows.length ? "1" : undefined} tabIndex={-1}
              className={`w-full text-left px-3 py-2.5 border-t border-slate-100 text-sm text-emerald-700 font-medium flex items-center gap-2 ${hi === rows.length ? "bg-emerald-50" : "hover:bg-emerald-50"}`}>
              <Plus className="w-4 h-4" /> Add new{text ? ` "${text}"` : ""}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
